import {
  GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsGroups, useGetPartnerAdminsPartnerFeatureFlags } from '~/hooks';
import { getClientLink } from '~/utils/getLinks';
import { generateRequest } from '../logics/generateRequest';
import { CreateClientFormFieldValues, createClientSchema } from '../logics/schema';
import { LegacyCreateClientFormPresentation } from '../presentations';
import { useGetPartnerAdminsClientFeatureFlags } from './useGetPartnerAdminsClientFeatureFlags';
import { usePostPartnerAdminsClients } from './usePostPartnerAdminsClients';

export { usePostPartnerAdminsClients } from './usePostPartnerAdminsClients';

type PresentationProps = React.ComponentProps<typeof LegacyCreateClientFormPresentation>;

type IUseLegacyCreateClientForm = () => {
  state: {
    methods: UseFormReturn<CreateClientFormFieldValues>;
    isButtonDisabled: boolean;
    groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
    clientFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200;
    partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * 利用企業作成フォームのカスタムフック（Legacy版）
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isButtonDisabled - ボタン無効化フラグ
 * @returns state.groups - グループ一覧
 * @returns state.clientFeatureFlags - クライアント機能フラグ一覧
 * @returns state.partnerFeatureFlags - パートナー機能フラグ一覧
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useLegacyCreateClientForm: IUseLegacyCreateClientForm = () => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data: groupsData } = useGetPartnerAdminsGroups({ threeLetterCode });
  const { data: clientFeatureFlagsData } = useGetPartnerAdminsClientFeatureFlags({
    threeLetterCode,
  });
  const { data: partnerFeatureFlagsData } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });

  const methods = useForm<CreateClientFormFieldValues>({
    defaultValues: {
      name: '',
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      corporateNumber: '',
      externalClientId: '',
      groupId: '',
      clientFeatureFlags: [],
      employeeCount: undefined,
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(createClientSchema),
  });

  const { trigger, isMutating } = usePostPartnerAdminsClients(threeLetterCode, {
    onSuccess: ({ client: { id, name } }) => {
      showToast({
        message: `「${name}」を追加しました。`,
        status: 'success',
      });
      navigate(getClientLink(id));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? '利用企業の追加に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: '利用企業の追加に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    const requestBody = generateRequest({
      formValues,
      featureFlags: clientFeatureFlagsData.client_feature_flags,
    });
    await trigger(requestBody);
  });
  const isButtonDisabled =
    methods.formState.isSubmitting || !methods.formState.isValid || isMutating;

  return {
    state: {
      methods,
      isButtonDisabled,
      groups: groupsData,
      clientFeatureFlags: clientFeatureFlagsData,
      partnerFeatureFlags: partnerFeatureFlagsData,
    },
    actions: { handleOnSubmit },
  };
};
